import React from 'react';
import {
	AdminSymbolPageSize,
	AdminSymbolSortField,
	IAdminSymbolFilters,
	IAdminSymbolSort,
	isAdminSymbolPageSize,
} from '../../api/admin';
import SavedPresetControls from './SavedPresetControls';

const VALID_SORT_FIELDS: Partial<Record<AdminSymbolSortField, true>> = {
	dataset_symbol_id: true,
	dataset_name: true,
	ds_type: true,
	ds_symbol_1: true,
	ds_symbol_2: true,
	symbol_metadata: true,
	first_seen: true,
	last_seen: true,
	company_name: true,
	is_active: true,
	start_date: true,
	end_date: true,
};

export interface SymbolsTableViewState {
	search: string;
	filters: IAdminSymbolFilters;
	sort: IAdminSymbolSort | null;
	pageSize: AdminSymbolPageSize;
}

function parseFilters(value: unknown): IAdminSymbolFilters | null {
	if (!value || typeof value !== 'object') return null;
	const filters = value as Record<string, unknown>;
	const datasetIds = Array.isArray(filters.datasetIds)
		? Array.from(new Set<number>(filters.datasetIds.filter(
			(datasetId): datasetId is number => Number.isSafeInteger(datasetId) && datasetId > 0,
		)))
		: null;
	if (!datasetIds) return null;

	const isFilterMode = (mode: unknown): mode is 'include' | 'exclude' => (
		mode === 'include' || mode === 'exclude'
	);
	const mappingStatus = filters.mappingStatus === undefined ? 'any' : filters.mappingStatus;
	if (mappingStatus !== 'any'
		&& mappingStatus !== 'active'
		&& mappingStatus !== 'inactive'
		&& mappingStatus !== 'unmapped') return null;
	const dsTypes = Array.isArray(filters.dsTypes)
		? Array.from(new Set<string>(filters.dsTypes.filter((dsType): dsType is string => typeof dsType === 'string')))
		: [];
	const stringFields = [
		'firstSeenFrom',
		'firstSeenTo',
		'lastSeenFrom',
		'lastSeenTo',
		'companyName',
	] as const;
	if (stringFields.some((field) => typeof filters[field] !== 'string')) return null;
	if (!isFilterMode(filters.firstSeenMode)
		|| !isFilterMode(filters.lastSeenMode)
		|| !isFilterMode(filters.companyNameMode)) return null;

	return {
		datasetIds,
		firstSeenFrom: filters.firstSeenFrom as string,
		firstSeenTo: filters.firstSeenTo as string,
		firstSeenMode: filters.firstSeenMode,
		lastSeenFrom: filters.lastSeenFrom as string,
		lastSeenTo: filters.lastSeenTo as string,
		lastSeenMode: filters.lastSeenMode,
		companyName: filters.companyName as string,
		companyNameMode: filters.companyNameMode,
		mappingStatus,
		dsTypes,
	};
}

function parseSort(value: unknown): IAdminSymbolSort | null | undefined {
	if (value === null) return null;
	if (!value || typeof value !== 'object') return undefined;
	const sort = value as { field?: unknown; direction?: unknown };
	if (typeof sort.field !== 'string'
		|| !VALID_SORT_FIELDS[sort.field as AdminSymbolSortField]
		|| (sort.direction !== 'asc' && sort.direction !== 'desc')) return undefined;
	return {
		field: sort.field as AdminSymbolSortField,
		direction: sort.direction,
	};
}

function parseSymbolsView(value: unknown): SymbolsTableViewState | null {
	if (!value || typeof value !== 'object') return null;
	const storedView = value as Record<string, unknown>;
	const filters = parseFilters(storedView.filters);
	const sort = parseSort(storedView.sort);
	if (typeof storedView.search !== 'string'
		|| !filters
		|| sort === undefined
		|| !isAdminSymbolPageSize(storedView.pageSize)) return null;
	return {
		search: storedView.search,
		filters,
		sort,
		pageSize: storedView.pageSize,
	};
}

interface Props {
	currentView: SymbolsTableViewState;
	onLoad: (view: SymbolsTableViewState) => void;
}

export default function SymbolsTableViews({ currentView, onLoad }: Props) {
	return (
		<SavedPresetControls
			storageNamespace="admin.symbols.tableViews.v3"
			noun="view"
			currentValue={currentView}
			parseValue={parseSymbolsView}
			onLoad={onLoad}
		/>
	);
}
